import {
  memo,
  useEffect,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames/bind';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight';

import { useHistory } from 'react-router-dom';
import VideoSlide from '../../LandingPages/LearnMoreComponents/MainLearnMore/LearnMoreSections/VideoSlide';
import Slider from '../../Shared/ReactSlick';

import Styles from './styles.module.scss';

const cx = classNames.bind(Styles);

const NextArrow = memo((props) => {
  const history = useHistory();
  const { nextVideo, tab } = props;

  const onHandleClick = () => {
    if (nextVideo === '') return;
    history.push(`/help-videos/${tab}/${nextVideo}/desktop`);
  };

  return (
    <div
      className={Styles['button-next']}
      onClick={onHandleClick}
    >
      <FontAwesomeIcon icon={faChevronRight} />
    </div>
  );
});

const PrevArrow = memo((props) => {
  const history = useHistory();
  const { prevVideo, tab } = props;

  const onHandleClick = () => {
    if (prevVideo === '') return;
    history.push(`/help-videos/${tab}/${prevVideo}/desktop`);
  };

  return (
    <div
      className={Styles['button-prev']}
      onClick={onHandleClick}
    >
      <FontAwesomeIcon icon={faChevronLeft} />
    </div>
  );
});

const HelpCenterSlider = ({
  subCategories,
  tab,
  video,
  device,
}) => {
  const [activeDeviceTab, setActiveDeviceTab] = useState('');
  const [prevVideo, setPrevVideo] = useState('');
  const [nextVideo, setNextVideo] = useState('');

  const [nav1, setNav1] = useState();
  const [nav2, setNav2] = useState();
  const history = useHistory();

  let slider1 = useRef();
  let slider2 = useRef();

  useEffect(() => {
    setNav1(slider1);
    setNav2(slider2);
  }, []);

  const settings = {
    fade: true,
    slidesToShow: 1,
    centerMode: true,
    infinite: false,
    nextArrow: <NextArrow nextVideo={nextVideo} tab={tab} />,
    prevArrow: <PrevArrow prevVideo={prevVideo} tab={tab} />,
    centerPadding: '35px',
    beforeChange: () => {
      setActiveDeviceTab('');
      setTimeout(() => {
        setActiveDeviceTab('desktop');
      }, 10);
    },
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          dots: false,
          fade: false,
          slidesToShow: 1,
          nextArrow: <NextArrow nextVideo={nextVideo} tab={tab} />,
          prevArrow: <PrevArrow prevVideo={prevVideo} tab={tab} />,
          centerPadding: '35px',
          beforeChange: () => {
            setActiveDeviceTab('');
            setTimeout(() => {
              setActiveDeviceTab('desktop');
            }, 100);
          },
        },
      },
    ],
  };

  const navSettings = {
    slidesToShow: subCategories?.length > 0 ? subCategories?.length : 5,
    swipeToSlide: true,
    focusOnSelect: true,
    centerMode: true,
    vertical: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          swipeToSlide: true,
          slidesToShow: subCategories?.length < 3 ? subCategories?.length : 3,
          vertical: false,
          dots: false,
          infinite: true,
        },
      },
      {
        breakpoint: 767,
        settings: {
          swipeToSlide: true,
          slidesToShow: 1,
          vertical: false,
          dots: false,
          infinite: true,
        },
      },
    ],
  };

  const handleDeviceChange = (device) => {
    setActiveDeviceTab(device);
    history.push(`/help-videos/${tab}/${video}/${device}`);
  };

  const handleSlideChange = (subTabPath) => {
    history.push(`/help-videos/${tab}/${subTabPath}/desktop`);
  };

  const findPrevAndNextVideo = () => {
    const currentVideoIndex = subCategories.findIndex((subCat) => subCat.subTabPath === video);

    if (currentVideoIndex === -1) return;

    if (currentVideoIndex !== 0) {
      const prevIndex = currentVideoIndex - 1;
      setPrevVideo(subCategories[prevIndex].subTabPath);
    }

    if (currentVideoIndex !== subCategories.length - 1) {
      const nextIndex = currentVideoIndex + 1;
      setNextVideo(subCategories[nextIndex].subTabPath);
    }
  };

  useEffect(() => {
    if (!video || !subCategories) return;
    if (subCategories[0].subTabPath !== video) return;
    findPrevAndNextVideo();
  }, [video, subCategories]);

  useEffect(() => {
    if (!video || !subCategories || !nav1) return;

    const currentVideoIndex = subCategories.map((subCategory) => subCategory.subTabPath)
      ?.findIndex((subTabPath) => subTabPath === video);

    nav1.slickGoTo(currentVideoIndex);
    findPrevAndNextVideo();
  }, [video, subCategories, nav1]);

  useEffect(() => {
    if (!device) return;

    if (device !== activeDeviceTab) {
      setActiveDeviceTab(device);
    }
  }, [device, activeDeviceTab]);

  return (
    <section className={cx('help_center_sub_category-section', 'help_center_sub_category')}>
      <div className={cx('sliders-holder')}>
        <Slider
          className={Styles['help_center_sub_category-slider']}
          ref={(slider) => { slider2 = slider; }}
          asNavFor={nav1}
          {...navSettings}
        >
          {subCategories?.map(({ id, title, subTabPath }) => (
            <div
              className={cx('main-slide')}
              key={id}
              onClick={() => handleSlideChange(subTabPath)}
            >
              <h6>
                {title}
              </h6>
            </div>
          ))}
        </Slider>
        <Slider
          className={Styles['help_center_sub_category-navigation-slider']}
          {...settings}
          asNavFor={nav2}
          ref={(slider) => { slider1 = slider; }}
        >
          {subCategories?.map((subCategory) => (
            <div className={Styles.slide} key={subCategory.id}>
              <div className={cx('tabs', { show: subCategory.hasTabs })}>
                <div
                  className={cx('tab', { active: device === 'desktop' })}
                  onClick={() => handleDeviceChange('desktop')}
                >
                  Desktop
                </div>
                <div
                  className={cx('tab', { active: device === 'mobile' })}
                  onClick={() => handleDeviceChange('mobile')}
                >
                  Mobile
                </div>
              </div>
              {subCategory.videos && subCategory.videos.map((
                { id, activeTab, link },
              ) => (
                <div key={id} hidden={activeTab && activeTab !== activeDeviceTab}>
                  <VideoSlide
                    key={id}
                    url={link}
                  />
                </div>
              ))}
            </div>
          ))}
        </Slider>
      </div>
    </section>
  );
};

export default HelpCenterSlider;
